/**
 * GovernmentIdentitySection — Identity block for the Government entity dialog.
 *
 * Per government.md §"Required fields (entity-level)":
 *   - Entity Name (e.g., "City of Rolla") — maps to `firstName` (legacy
 *     `name1Label='Entity Name'` per entityGroupConfig.ts:52)
 *   - Government Level dropdown — Federal / State / County / Municipal /
 *     School District / Special District (stored in extra.governmentLevel)
 *   - Entity Sub-type dropdown — PublicFunds / Donation / ICS_Personal
 *     (stored in extra.governmentSubType; drives derivation)
 *   - Donation variant radio (conditional — only when sub-type=Donation)
 *   - Federal Tax ID / EIN (taxId)
 *   - Finance Officer Name (extra.financeOfficerName)
 *   - Finance Officer Title (extra.financeOfficerTitle)
 *
 * Sub-type is MANUALLY SELECTED (unlike Individual which auto-derives). The
 * derivation hook observes the officer's choice and publishes callClass /
 * tinCode / cddExempt / etc. back via onChange.
 *
 * Finance-only banner (ICS_Personal): red "FSR should not open" banner
 * surfaces per government.md §"Form layout suggestion" point 1.
 *
 * TODO(bill): government walkthrough needed — finalize sub-type picker copy
 * + donation short/long radio visibility rules. See government.md §Open
 * Questions #3.
 */

import { useEffect } from 'react';
import { Pill } from '@/components/primitives';
import type { RaoPerson, RaoTinCode } from '../../../types';
import type { DerivationResult } from './useGovernmentDerivation';
import type { GovernmentSubType, DonationVariant } from './government-config';
import {
  GOVERNMENT_LEVELS,
  GOVERNMENT_SUB_TYPES,
  DONATION_VARIANTS,
  ENTITY_NAME_HELPER_TEXT,
  FINANCE_OFFICER_HELPER_TEXT,
  EIN_HELPER_TEXT,
} from './government-config';

// -----------------------------------------------------------------------------
// Leaf styles (consistent with Individual sections)
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers
// -----------------------------------------------------------------------------

function readExtraString(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function extraPatch(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface GovernmentIdentitySectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  derivation: DerivationResult;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function GovernmentIdentitySection({
  person,
  onChange,
  mode,
  isChanged,
  derivation,
  readOnly = false,
}: GovernmentIdentitySectionProps): JSX.Element {
  const isExisting = mode === 'existing';
  const ic = isChanged ?? ((_: string) => false);

  const govLevel = readExtraString(person, 'governmentLevel');
  const financeOfficerName = readExtraString(person, 'financeOfficerName');
  const financeOfficerTitle = readExtraString(person, 'financeOfficerTitle');

  // Keep person.customerType + callClass + cdd flags synced with derivation.
  // Sub-type is officer-driven; we push the derived canonical values so the
  // cart envelope + save dispatch see authoritative values.
  useEffect(() => {
    const patch: Partial<RaoPerson> = {};
    if (person.customerType !== derivation.customerType) {
      patch.customerType = derivation.customerType;
    }
    if (person.callClass !== derivation.callClass) {
      patch.callClass = derivation.callClass;
    }
    if (person.tinCode !== derivation.tinCode) {
      patch.tinCode = derivation.tinCode as RaoTinCode;
    }
    if ((person.cddExempt ?? false) !== derivation.cddExempt) {
      patch.cddExempt = derivation.cddExempt;
    }
    if (
      derivation.cddExemptReasonCode &&
      person.cddExemptReasonCode !== derivation.cddExemptReasonCode
    ) {
      patch.cddExemptReasonCode = derivation.cddExemptReasonCode;
    }
    if (Object.keys(patch).length > 0) {
      onChange(patch);
    }
  }, [
    derivation.customerType,
    derivation.callClass,
    derivation.tinCode,
    derivation.cddExempt,
    derivation.cddExemptReasonCode,
    person.customerType,
    person.callClass,
    person.tinCode,
    person.cddExempt,
    person.cddExemptReasonCode,
    onChange,
  ]);

  const setSubType = (value: GovernmentSubType): void => {
    onChange(extraPatch(person, { governmentSubType: value }));
  };

  const setDonationVariant = (value: DonationVariant): void => {
    onChange(extraPatch(person, { donationVariant: value }));
  };

  const setGovLevel = (value: string): void => {
    onChange(extraPatch(person, { governmentLevel: value }));
  };

  const setFinanceOfficerName = (value: string): void => {
    onChange(extraPatch(person, { financeOfficerName: value }));
  };

  const setFinanceOfficerTitle = (value: string): void => {
    onChange(extraPatch(person, { financeOfficerTitle: value }));
  };

  const currentSubTypeOption = GOVERNMENT_SUB_TYPES.find(
    (s) => s.value === derivation.governmentSubType,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Finance-only blocking banner (ICS_Personal) */}
      {derivation.financeOnly && (
        <div
          role="alert"
          style={{
            padding: '12px 16px',
            background: 'var(--err-bg)',
            border: '1px solid var(--pcb-red, #b10505)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>ICS Personal is Finance-Department initiated only.</strong>{' '}
          FSRs should not open ICS_Personal accounts via Deposit Pro. Contact
          the Finance Department for pre-vetted account details.
        </div>
      )}

      {/* Finance-approval gate banner */}
      {derivation.requiresFinanceApproval && !derivation.financeOnly && (
        <div
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Finance approval required.</strong> This sub-type requires
          Finance Department approval + bid documentation before account open.
        </div>
      )}

      {/* Read-only entity type chip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'var(--info-bg)',
          border: '1px solid var(--info-line)',
          borderRadius: 6,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>
          Entity Type:
        </span>
        <Pill variant="info">{derivation.customerType}</Pill>
        <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>
          · Sub-type: <strong>{derivation.governmentSubType}</strong>
        </span>
        <span style={{ fontSize: 11, color: 'var(--ink-500)', marginLeft: 'auto' }}>
          Call Class {derivation.callClass} · TIN {derivation.tinCode}
          {derivation.cddExempt && <> · CDD Exempt</>}
        </span>
      </div>

      {/* Sub-type picker */}
      <div>
        <label style={labelStyle} htmlFor="rao-gov-subtype">
          Entity Sub-type
        </label>
        <select
          id="rao-gov-subtype"
          value={derivation.governmentSubType}
          onChange={(e) => setSubType(e.currentTarget.value as GovernmentSubType)}
          disabled={readOnly || isExisting}
          style={changedFieldStyle(
            readOnly || isExisting ? readOnlyStyle : inputStyle,
            ic('extra.governmentSubType'),
          )}
          title="Government sub-type"
        >
          {GOVERNMENT_SUB_TYPES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        {currentSubTypeOption && (
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>
            {currentSubTypeOption.helpText}
          </div>
        )}
      </div>

      {/* Donation variant radio (conditional) */}
      {derivation.governmentSubType === 'Donation' && (
        <div
          style={{
            padding: '10px 12px',
            background: 'var(--info-bg)',
            border: '1px solid var(--info-line)',
            borderRadius: 6,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 8 }}>
            Donation Variant
            {/* TODO(bill): government walkthrough needed — confirm variant labels + rules */}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {DONATION_VARIANTS.map((v) => (
              <label
                key={v.value}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  fontSize: 13,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                  maxWidth: 340,
                }}
              >
                <input
                  type="radio"
                  name="rao-gov-donation-variant"
                  value={v.value}
                  checked={derivation.donationVariant === v.value}
                  onChange={() => setDonationVariant(v.value)}
                  disabled={readOnly}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>{v.label}</strong>
                  <br />
                  <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>
                    {v.helpText}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Entity Name row */}
      <div>
        <label style={labelStyle} htmlFor="rao-gov-entity-name">
          Entity Name
        </label>
        <input
          id="rao-gov-entity-name"
          type="text"
          value={person.firstName ?? ''}
          onChange={(e) => onChange({ firstName: e.currentTarget.value })}
          readOnly={readOnly}
          style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('firstName'))}
          placeholder='e.g., "City of Rolla"'
          maxLength={40}
          title="Legal government-entity name."
        />
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>
          {ENTITY_NAME_HELPER_TEXT}
        </div>
      </div>

      {/* Department / Division + Government Level row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-name2">
            Department / Division
          </label>
          <input
            id="rao-gov-name2"
            type="text"
            value={person.name2 ?? ''}
            onChange={(e) => onChange({ name2: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('name2'))}
            placeholder="e.g., Finance Department"
            maxLength={40}
            title="Department or division (Core Director Field 8)."
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-level">
            Government Level
          </label>
          <select
            id="rao-gov-level"
            value={govLevel}
            onChange={(e) => setGovLevel(e.currentTarget.value)}
            disabled={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('extra.governmentLevel'))}
            title="Government jurisdictional level."
          >
            <option value="">Select level...</option>
            {GOVERNMENT_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* EIN / TIN row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-ein">
            Federal Tax ID / EIN
          </label>
          <input
            id="rao-gov-ein"
            type="text"
            value={person.taxId ?? ''}
            onChange={(e) =>
              onChange({ taxId: e.currentTarget.value.replace(/\D/g, '').slice(0, 9) })
            }
            readOnly={isExisting || readOnly}
            style={changedFieldStyle(
              isExisting || readOnly ? readOnlyStyle : inputStyle,
              ic('taxId'),
            )}
            maxLength={9}
            placeholder="123456789"
            title="Federal Employer Identification Number"
          />
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>
            {EIN_HELPER_TEXT}
          </div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-gov-tincode">
            TIN code
          </label>
          <select
            id="rao-gov-tincode"
            value={person.tinCode ?? derivation.tinCode}
            disabled
            style={readOnlyStyle}
            title="TIN code derived from sub-type."
          >
            <option value="S">S · SSN</option>
            <option value="E">E · EIN</option>
            <option value="N">N · None</option>
          </select>
        </div>
      </div>

      {/* Finance Officer row */}
      <div>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginBottom: 6, fontStyle: 'italic' }}>
          {FINANCE_OFFICER_HELPER_TEXT}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle} htmlFor="rao-gov-fo-name">
              Finance Officer Name
            </label>
            <input
              id="rao-gov-fo-name"
              type="text"
              value={financeOfficerName}
              onChange={(e) => setFinanceOfficerName(e.currentTarget.value)}
              readOnly={readOnly}
              style={changedFieldStyle(
                readOnly ? readOnlyStyle : inputStyle,
                ic('extra.financeOfficerName'),
              )}
              placeholder="e.g., Jane Smith"
              maxLength={80}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="rao-gov-fo-title">
              Finance Officer Title
            </label>
            <input
              id="rao-gov-fo-title"
              type="text"
              value={financeOfficerTitle}
              onChange={(e) => setFinanceOfficerTitle(e.currentTarget.value)}
              readOnly={readOnly}
              style={changedFieldStyle(
                readOnly ? readOnlyStyle : inputStyle,
                ic('extra.financeOfficerTitle'),
              )}
              placeholder="e.g., City Treasurer"
              maxLength={40}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
